import { Component, ViewChild, OnInit, inject } from '@angular/core';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatPaginatorModule, MatPaginator } from '@angular/material/paginator';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatTooltip } from "@angular/material/tooltip";
import { CommonModule } from '@angular/common';
import { AdminService } from '@adgate/modules/admin/services/admin-service';
import { AdminPanelModule } from '@adgate/modules/admin/types/admin-panel-module.interface';
import { RESTApplicationResponse } from '@adgate/common/types/rest-application-response.type';
import { DialogService } from '@adgate/common/services/dialog-service';
import { CommercialOffice } from '@adgate/common/types/commercial-office.type';
import { filter, switchMap } from 'rxjs';
import { OfficeEditDialogComponent } from '@adgate/modules/admin/dialogs/office-edit-dialog-component/office-edit-dialog-component';
import { QuickSearch } from '@adgate/common/interfaces/quick-search.interface';

@Component({
  selector: 'app-admin-panel-offices-component',
  imports: [
    CommonModule,
    MatTableModule,
    MatSortModule,
    MatPaginatorModule,
    MatCheckboxModule,
    MatTooltip
  ],
  templateUrl: './admin-panel-offices-component.html',
  styleUrl: './admin-panel-offices-component.scss',
})
export class AdminPanelOfficesComponent implements OnInit, AdminPanelModule, QuickSearch
{
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;

  private adminService: AdminService = inject(AdminService);
  private dialogService: DialogService = inject(DialogService);

  displayedColumns: string[] = ['name', 'externalId', 'actions'];
  dataSource: MatTableDataSource<CommercialOffice> = new MatTableDataSource<CommercialOffice>();

  constructor()
  {

  }

  ngOnInit(): void
  {
    this.onRefresh();
  }

  ngAfterViewInit()
  {
    this.dataSource.sortingDataAccessor = this.getTableDataAccessor();
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  onRefresh(): void
  {
    this.dialogService.showProgress();
    this.adminService.getCommercialOffices().subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.hideProgress();
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  getQuickSearchPlaceholder(): string 
  {
    return 'Search Offices';
  }

  onQuickSearch(filter: string | null)
  {
    this.dataSource.filter = filter!;
  }

  onNewOffice(): void
  {
    this.onEditOffice(new CommercialOffice());
  }

  onEditOffice(office: CommercialOffice): void
  {
    let successMessage: string;

    const dialogRef = this.dialogService.openAnyDialog(OfficeEditDialogComponent, {
      data: office,
      width: '700px',
      maxWidth: '90vw',
      disableClose: true
    });

    dialogRef.afterClosed().pipe(
      filter((result: CommercialOffice | null ) => result !== null),
      switchMap((result: CommercialOffice) => {
        this.dialogService.showProgress();
        return this.adminService.updateCommercialOffice(result);
      }),
      switchMap((r:RESTApplicationResponse) => {
        successMessage = r.message;
        return this.adminService.getCommercialOffices();
      })
    ).subscribe({
      next: (r:RESTApplicationResponse) => {
        this.dataSource.data = r.data;
        this.dialogService.openInfoDialog(successMessage);
      },
      error: (err:Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  onDeleteOffice(row: CommercialOffice): void
  {
    const dialogRef = this.dialogService.openConfirmDialog(
      `Delete office "${row.description}"?`,
      {
        title: 'Delete Office',
        confirmText: 'Delete',
        cancelText: 'Cancel',
        severity: 'danger'
      }
    );

    dialogRef.afterClosed().pipe(
      filter((confirmed) => confirmed === true),
      switchMap(() => {
        this.dialogService.showProgress();
        return this.adminService.deleteCommercialOffice(row.id);
      })
    ).subscribe({
      next: (r: RESTApplicationResponse) => {
        this.dialogService.openInfoDialog(r.message);
        this.onRefresh();
      },
      error: (err: Error) => {
        this.dialogService.openErrorDialog(err.message);
      }
    });
  }

  getTableDataAccessor(): any
  {
    return (item: any, property: any) => {
      switch(property) {
        case 'name': return item.description;
        case 'externalId': return item.externalId;
        default: return item[property];
      }
    };
  }
}
